import { useComparer } from '@/hooks/useComparer';
import { useExpandable } from '@/hooks/useExpandable';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import { ComparerSubroutine } from '@/types/comparer';
import { sortBy } from '@/util/application';
import { Chip, Typography } from '@mui/material';
import { type FC } from 'react';
import { getChangedItems, getVisibleItems, getVisibleParams, statusColors } from '../comparerUtils/comparerUtils';
import CategoryChanges from './CategoryChanges';
import HeaderModifiedProps from '../comparerShared/HeaderModifiedProps';
import NodesChanges from './NodesChanges';
import SectionHeader from '../comparerShared/SectionHeader';

type SubroutinesChangesProps = {
  subroutines: ComparerSubroutine[];
};

type SubroutineChangesProps = {
  subroutine: ComparerSubroutine;
};

const SubroutineChanges: FC<SubroutineChangesProps> = ({ subroutine }) => {
  const { showUnchanged } = useComparer();

  const [open, setOpen] = useExpandable(subroutine.status !== 'unchanged');
  const visibleNodes = subroutine.changes && getChangedItems(subroutine.changes.nodeChanges);

  return (
    <>
      <SectionHeader open={open} setOpen={setOpen} expandable={Boolean(subroutine.changes)}>
        <Chip label={subroutine.status} color={statusColors(subroutine.status)} size='small' sx={{ mr: 1 }} />
        <Typography variant='subtitle2' fontWeight={700}>
          Subroutine: {subroutine.label}
        </Typography>
        {subroutine.status === 'modified' && (
          <Typography variant='caption' color='text.secondary'>
            {visibleNodes?.length} node{visibleNodes?.length !== 1 ? 's' : ''} changed
          </Typography>
        )}
      </SectionHeader>
      {open &&
        getVisibleParams(subroutine.changes?.changedProps, showUnchanged).map((changes) => (
          <HeaderModifiedProps key={changes.key} param={changes} />
        ))}
      {open &&
        sortBy('id', subroutine.changes?.argChanges || []).map((subArg) => (
          <CategoryChanges key={subArg.id} category={subArg} type='arg' />
        ))}
      {open &&
        sortBy('id', subroutine.changes?.eventArgChanges || []).map((subEventArg) => (
          <CategoryChanges key={subEventArg.id} category={subEventArg} type='event arg' />
        ))}
      {open &&
        sortBy('id', subroutine.changes?.returnTypeChanges || []).map((subReturnType) => (
          <CategoryChanges key={subReturnType.id} category={subReturnType} type='return type' />
        ))}
      {open && <NodesChanges subroutines={subroutine} />}
    </>
  );
};

const SubroutinesChanges: FC<SubroutinesChangesProps> = ({ subroutines }) => {
  const { showUnchanged } = useComparer();

  const visibleSubroutines = useProgressiveList(getVisibleItems(subroutines, showUnchanged));

  return visibleSubroutines.map((subroutine) => <SubroutineChanges key={subroutine.id} subroutine={subroutine} />);
};

export default SubroutinesChanges;
